import { checkSearchTemplates, updateSearchTemplate } from '@/apis/searchtemplates';
import { queryClient } from '@/App';
import { SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN, SEARCH_TEMPLATE_NAME_MAX_LEN } from '@/constants';
import { ApiBodyError, isDefinedError } from '@/errors';
import { SearchTreeNode } from '@/types';
import { AgDialog, useSaladBar } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { TextField } from '@mui/material';
import { searchTreeBuilderStateAtom } from '@pages/AdvancedSearchListCreatorPage';
import { searchTemplateForCopyAtom } from '@pages/SavedSearchesPage';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { TemplatePlaceholderWithNodeAndFilterContext } from './types';
import { createSearchTemplateJson } from './util';

const SEARCH_TEMPLATE_DUPLICATE_MESSAGE = 'Template name is already in use.';

export type UpdateTemplateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** When update succeeds, called with the path to navigate to. Parent clears dirty and navigates. */
  onSaveSuccess?: (targetPath: string) => void;
  searchTemplateId: number;
  searchTree: SearchTreeNode | undefined;
  placeholdersData: Record<string, TemplatePlaceholderWithNodeAndFilterContext>;
  organisations: string[];
  initialDescription: string;
  initialName: string;
};

export default function UpdateTemplateDialog({
  isOpen,
  onClose,
  onSaveSuccess,
  searchTree,
  placeholdersData,
  organisations,
  initialName,
  initialDescription,
  searchTemplateId,
}: UpdateTemplateDialogProps) {
  const { enqueueSuccessNotification } = useSaladBar();
  const { getAccessTokenSilently } = useAuth0();
  const setSearchTreeBuilderState = useSetAtom(searchTreeBuilderStateAtom);
  const setSearchTemplateForCopyValue = useSetAtom(searchTemplateForCopyAtom);

  const [name, setname] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [isUpdateTemplateError, setIsUpdateTemplateError] = useState(false);
  const [updateTemplateErrorText, setUpdateTemplateErrorText] = useState('');
  const [isDuplicateDetected, setIsDuplicateDetected] = useState(false);
  const [isDescriptionTooShort, setIsDescriptionTooShort] = useState(false);

  const handleUpdateSearchTemplate = async () => {
    try {
      setIsUpdatingTemplate(true);
      const searchTemplateJson = createSearchTemplateJson(searchTree, placeholdersData);
      await updateSearchTemplate(await getAccessTokenSilently(), searchTemplateId, {
        name,
        description,
        searchTemplateJson,
        organisations,
      });
      setSearchTreeBuilderState(undefined);
      setSearchTemplateForCopyValue(undefined);
      await queryClient.invalidateQueries({ queryKey: ['searchtemplates'] });
      await queryClient.invalidateQueries({ queryKey: ['searchtemplate', searchTemplateId] });
      enqueueSuccessNotification('The template has been saved.');
      const targetPath = `/template/${searchTemplateId}`;
      onSaveSuccess?.(targetPath);
      onClose();
    } catch (e) {
      setIsUpdatingTemplate(false);
      setIsUpdateTemplateError(true);
      if (
        e instanceof ApiBodyError &&
        isDefinedError(e) &&
        e.body.errors[0]?.name === 'UpdateSearchTemplateDuplicateNameError'
      ) {
        setUpdateTemplateErrorText(SEARCH_TEMPLATE_DUPLICATE_MESSAGE);
      } else {
        setUpdateTemplateErrorText('There was an issue with the backend.');
      }
    }
  };

  const checkNameClashOnBlurOrEnter = async () => {
    if (!name) return;
    if (name === initialName) return;
    const token = await getAccessTokenSilently();
    const checkSearchResult = await checkSearchTemplates(token, { name: name });
    setIsDuplicateDetected(checkSearchResult);
  };

  const checkDescriptionOnBlurOrEnter = async () => {
    if (description.length < 3) {
      setIsDescriptionTooShort(true);
    } else {
      setIsDescriptionTooShort(false);
    }
  };

  return (
    <AgDialog
      isOpen={isOpen}
      onClose={onClose}
      dialogTitle="Update search template"
      primaryButton={{
        text: 'Update',
        onClick: handleUpdateSearchTemplate,
        disabled:
          name.length < 3 || isDescriptionTooShort || isUpdatingTemplate || isDuplicateDetected,
      }}
      secondaryButton={{
        text: 'Cancel',
        onClick: async () => {
          onClose();
        },
        disabled: isUpdatingTemplate,
      }}
      data-testid="qadUpdateSearchTemplateDialog"
    >
      <TextField
        margin="dense"
        id="name"
        label={'Template name'}
        type="text"
        fullWidth
        variant="standard"
        value={name}
        onChange={(event) => setname(event.target.value)}
        slotProps={{
          htmlInput: {
            maxLength: SEARCH_TEMPLATE_NAME_MAX_LEN,
          },
        }}
        error={isDuplicateDetected ? true : isUpdateTemplateError}
        helperText={
          isDuplicateDetected ? SEARCH_TEMPLATE_DUPLICATE_MESSAGE : updateTemplateErrorText
        }
        onBlur={checkNameClashOnBlurOrEnter}
        onKeyUp={async (event) => {
          if (event.key === 'Enter') {
            await checkNameClashOnBlurOrEnter();
          }
        }}
      />
      <TextField
        margin="dense"
        id="description"
        label={'Description'}
        type="text"
        fullWidth
        variant="filled"
        multiline
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        minRows={2}
        slotProps={{
          htmlInput: {
            maxLength: SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN,
          },
        }}
        onBlur={checkDescriptionOnBlurOrEnter}
        onKeyUp={async (event) => {
          if (event.key === 'Enter') {
            await checkDescriptionOnBlurOrEnter();
          }
        }}
      />
      <em>Note: this will overwrite the existing saved template and cannot be undone.</em>
    </AgDialog>
  );
}
